"use client";

import { useState } from "react";

/**
 * Quick reply option chips with optional free text input.
 *
 * Extracted from `compass-intake.jsx:597-623`.
 * Shows a set of tappable reply options for the user to choose from.
 */

export interface SuggestedReplyProps {
  /** Available reply options. */
  options: string[];
  /** Index of the currently selected option. */
  selected?: number;
  /** Placeholder for a free text alternative. */
  placeholder?: string;
  /** Called when an option chip is clicked. */
  onSelect?: (index: number) => void;
  /**
   * Called when the user submits a free-text reply — either by pressing Enter
   * or by blurring the input with a non-empty value.
   */
  onFreeText?: (value: string) => void;
}

/**
 * Quick-reply chips — a row of selectable suggestion buttons.
 */
export function SuggestedReply({
  options,
  selected,
  placeholder,
  onSelect,
  onFreeText,
}: SuggestedReplyProps) {
  const [freeText, setFreeText] = useState("");

  function submitFreeText(value: string) {
    const trimmed = value.trim();
    if (trimmed) {
      onFreeText?.(trimmed);
      setFreeText("");
    }
  }

  return (
    <div className="flex flex-col gap-[7px]">
      {options.map((opt, i) => {
        const isOn = selected === i;
        return (
          <button
            key={i}
            type="button"
            className={[
              "text-left",
              "font-[family-name:var(--font-body)] text-[13px] font-medium",
              "bg-(--bg-canvas) border rounded-(--radius-sm)",
              "px-[12px] py-[10px]",
              "cursor-pointer transition-all duration-300",
              isOn
                ? "border-(--fg-brand) bg-(--bg-sky) text-(--fg-primary) font-semibold"
                : "border-(--border-subtle) text-(--fg-secondary) hover:border-(--border-strong) hover:bg-(--bg-sky) hover:text-(--fg-primary)",
            ].join(" ")}
            onClick={() => onSelect?.(i)}
          >
            {opt}
          </button>
        );
      })}

      {placeholder && (
        <input
          type="text"
          value={freeText}
          className={[
            "border border-(--border-default) rounded-(--radius-sm)",
            "bg-(--bg-white) px-[12px] py-[10px]",
            "font-[family-name:var(--font-body)] text-[13px] text-(--fg-primary)",
            "placeholder:text-(--fg-muted)",
            "outline-none",
            "focus:border-(--fg-brand) focus:shadow-[inset_0_0_0_1px_var(--fg-brand)]",
          ].join(" ")}
          placeholder={placeholder}
          onChange={(e) => setFreeText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitFreeText(freeText);
            }
          }}
          onBlur={() => submitFreeText(freeText)}
        />
      )}
    </div>
  );
}
